import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MuiTextField from '@material-ui/core/TextField';
import MuiAutocomplete from '@material-ui/lab/Autocomplete';
import { Chip, Typography, Box, CircularProgress } from '@material-ui/core';
import { FieldValidation } from '@rjsf/utils';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

const CREATE_NEW_SENTINEL = '+ Create New Approver Set';

export interface ApproverSetData {
  setName: string;
  isNew: boolean;
  owners?: string[];
  ownerNames?: string[];
}

interface FetchedSet {
  members: { label: string }[];
}

export function ApproverSetPicker({
  onChange,
  rawErrors,
  required,
  formData,
  schema: { title, description },
}: FieldExtensionComponentProps<ApproverSetData>): JSX.Element {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [fetchedSets, setFetchedSets] = useState<Record<string, FetchedSet>>({});
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  // Catalog user entities for the owners multiselect
  const [userEntities, setUserEntities] = useState<Entity[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);

  const isCreatingNew = formData?.isNew ?? false;
  const selectedSetName = isCreatingNew ? CREATE_NEW_SENTINEL : (formData?.setName ?? '');

  const fetchSets = useCallback(async () => {
    if (hasFetched) return;
    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('github-data');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/oig-approver-sets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const { sets } = await resp.json();

        // Resolve member emails to display names via the catalog
        try {
          const catalogResult = await catalogApi.getEntities({ filter: { kind: 'User' } });
          const emailToName = new Map<string, string>();
          for (const entity of catalogResult.items) {
            const profile = (entity.spec as any)?.profile;
            const email = profile?.email;
            const displayName = profile?.displayName;
            if (email && displayName) {
              emailToName.set(email.toLowerCase(), displayName);
            }
          }
          for (const setData of Object.values(sets) as FetchedSet[]) {
            for (const member of setData.members) {
              const name = emailToName.get(member.label.toLowerCase());
              if (name) {
                member.label = `${name} (${member.label})`;
              }
            }
          }
        } catch {
          // Catalog unavailable — show raw emails as fallback
        }

        setFetchedSets(sets);
      }
    } catch {
      // Plugin not running or network error — picker falls back to manual entry
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [discoveryApi, identityApi, hasFetched]);

  // Preload approver sets when the component mounts
  useEffect(() => { fetchSets(); }, []);

  const existingSetNames = useMemo(
    () => Object.keys(fetchedSets).sort(),
    [fetchedSets],
  );

  const dropdownOptions = useMemo(
    () => [CREATE_NEW_SENTINEL, ...existingSetNames],
    [existingSetNames],
  );

  const selectedMembers = !isCreatingNew && formData?.setName && fetchedSets[formData.setName]?.members;

  const handleOptionChange = useCallback(
    (_event: any, value: string | null) => {
      if (value === CREATE_NEW_SENTINEL) {
        onChange({ setName: '', isNew: true, owners: formData?.owners ?? [], ownerNames: formData?.ownerNames ?? [] });
      } else {
        const setName = value ?? '';
        // Extract display names from the resolved member labels for poc:support
        const members = fetchedSets[setName]?.members ?? [];
        const memberNames = members.map(m => {
          // Labels are "First Last (email)" — extract just the name
          const parenIdx = m.label.indexOf(' (');
          return parenIdx > 0 ? m.label.substring(0, parenIdx) : m.label;
        });
        onChange({ setName, isNew: false, ownerNames: memberNames });
      }
    },
    [onChange, formData?.owners, formData?.ownerNames, fetchedSets],
  );

  const handleNewNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ setName: e.target.value, isNew: true, owners: formData?.owners ?? [], ownerNames: formData?.ownerNames ?? [] });
    },
    [onChange, formData?.owners],
  );

  const handleOwnersChange = useCallback(
    (_event: any, value: any) => {
      const entities = value as Entity[];
      const refs = entities.map((e: Entity) => stringifyEntityRef(e));
      const names = entities.map((e: Entity) => getUserDisplayName(e));
      onChange({ setName: formData?.setName ?? '', isNew: true, owners: refs, ownerNames: names });
    },
    [onChange, formData?.setName],
  );

  // Fetch catalog User entities when "Create New" is selected
  const fetchUsers = useCallback(async () => {
    if (usersFetched) return;
    setUsersLoading(true);
    try {
      const result = await catalogApi.getEntities({ filter: { kind: 'User' } });
      const sorted = result.items.sort((a, b) => {
        const nameA = getUserDisplayName(a);
        const nameB = getUserDisplayName(b);
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      });
      setUserEntities(sorted);
    } catch {
      // Catalog unavailable — owners input will have no suggestions
    } finally {
      setUsersLoading(false);
      setUsersFetched(true);
    }
  }, [catalogApi, usersFetched]);

  // Trigger user fetch when switching to "Create New"
  useEffect(() => {
    if (isCreatingNew && !usersFetched) {
      fetchUsers();
    }
  }, [isCreatingNew, usersFetched, fetchUsers]);

  // Map selected owner refs back to entities for the Autocomplete value
  const selectedOwnerEntities = useMemo(() => {
    const ownerRefs = formData?.owners ?? [];
    return ownerRefs
      .map(ref => userEntities.find(e => stringifyEntityRef(e) === ref))
      .filter((e): e is Entity => !!e);
  }, [formData?.owners, userEntities]);

  function getUserDisplayName(entity: Entity): string {
    const profile = (entity.spec as any)?.profile;
    return profile?.displayName || entity.metadata.name;
  }

  return (
    <Box>
      <MuiAutocomplete
        value={selectedSetName}
        options={dropdownOptions}
        loading={loading}
        getOptionLabel={option => option}
        onChange={handleOptionChange}
        onOpen={fetchSets}
        renderInput={params => (
          <MuiTextField
            {...params}
            label={title ?? 'Approver Set'}
            helperText={description}
            margin="dense"
            variant="outlined"
            required={required}
            error={!!rawErrors?.length}
          />
        )}
      />

      {/* Show members of the selected existing set */}
      {selectedMembers && selectedMembers.length > 0 && (
        <Box mt={1} mb={1}>
          <Typography variant="caption" color="textSecondary">
            Members of &ldquo;{formData?.setName}&rdquo;:
          </Typography>
          <Box mt={0.5} display="flex" flexWrap="wrap" style={{ gap: 4 }}>
            {selectedMembers.map(m => (
              <Chip key={m.label} label={m.label} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}

      {/* New set: name + owners inputs */}
      {isCreatingNew && (
        <Box mt={1}>
          <MuiTextField
            fullWidth
            label="New approver set name"
            value={formData?.setName ?? ''}
            onChange={handleNewNameChange}
            margin="dense"
            variant="outlined"
            required
          />
          <Box mt={1}>
            <MuiAutocomplete
              multiple
              value={selectedOwnerEntities}
              options={userEntities}
              loading={usersLoading}
              getOptionLabel={(option: Entity) => getUserDisplayName(option)}
              filterSelectedOptions
              onChange={handleOwnersChange}
              renderTags={(value, getTagProps) =>
                value.map((entity, index) => (
                  <Chip
                    label={getUserDisplayName(entity)}
                    size="small"
                    {...getTagProps({ index })}
                    key={stringifyEntityRef(entity)}
                  />
                ))
              }
              renderInput={params => (
                <MuiTextField
                  {...params}
                  label="Account owners"
                  helperText="Search and select users who should be in this approver set"
                  margin="dense"
                  variant="outlined"
                  required={selectedOwnerEntities.length === 0}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export const ApproverSetPickerSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'object',
    title: 'Approver set selection',
    properties: {
      setName: { type: 'string' },
      isNew: { type: 'boolean' },
      owners: { type: 'array', items: { type: 'string' } },
    },
  },
};

export const ApproverSetPickerValidation = (
  formData: ApproverSetData,
  validation: FieldValidation,
): void => {
  if (!formData?.setName || formData.setName.trim() === '') {
    validation.addError('You must select an approver set or create a new one.');
  }
  if (formData?.isNew && (!formData.owners || formData.owners.length === 0)) {
    validation.addError('New approver sets must have at least one owner.');
  }
};
